import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  // Chart of Accounts
  chartOfAccounts: [],
  
  // Journal Entries
  journalEntries: [],
  currentEntry: null,
  
  // Financial Reports
  balanceSheet: null,
  incomeStatement: null,
  trialBalance: null,
  cashFlowStatement: null,
  paymentAccounts: null,
  generalLedger: null,
  dashboardData: null,
  
  // Period Management
  currentPeriod: null,
  financialPeriods: [],
  
  // UI State
  loading: {
    accounts: false,
    entries: false,
    reports: false,
    balanceSheet: false,
    incomeStatement: false,
    trialBalance: false,
    cashFlow: false,
    paymentAccounts: false
  },
  error: null,
  
  // Filters and Search
  filters: {
    dateRange: { start: null, end: null },
    accountType: 'all',
    status: 'all',
    period: 'current'
  },
  
  // Print States
  printData: {
    balanceSheet: null,
    incomeStatement: null,
    trialBalance: null,
    cashFlow: null,
    paymentAccounts: null
  }
};

const accountingSlice = createSlice({
  name: 'accounting',
  initialState,
  reducers: {
    // Chart of Accounts actions
    setChartOfAccounts: (state, action) => {
      state.chartOfAccounts = action.payload;
    },
    addAccount: (state, action) => {
      state.chartOfAccounts.push(action.payload);
    },
    updateAccount: (state, action) => {
      const index = state.chartOfAccounts.findIndex(acc => acc._id === action.payload._id);
      if (index !== -1) {
        state.chartOfAccounts[index] = action.payload;
      }
    },
    deleteAccount: (state, action) => {
      state.chartOfAccounts = state.chartOfAccounts.filter(acc => acc._id !== action.payload);
    },
    
    // Journal Entries actions
    setJournalEntries: (state, action) => {
      state.journalEntries = action.payload;
    },
    addJournalEntry: (state, action) => {
      state.journalEntries.unshift(action.payload);
    },
    updateJournalEntry: (state, action) => {
      const index = state.journalEntries.findIndex(entry => entry._id === action.payload._id);
      if (index !== -1) {
        state.journalEntries[index] = action.payload;
      }
    },
    deleteJournalEntry: (state, action) => {
      state.journalEntries = state.journalEntries.filter(entry => entry._id !== action.payload);
    },
    setCurrentEntry: (state, action) => {
      state.currentEntry = action.payload;
    },
    
    // Financial Reports actions
    setBalanceSheet: (state, action) => {
      state.balanceSheet = action.payload;
    },
    setIncomeStatement: (state, action) => {
      state.incomeStatement = action.payload;
    },
    setTrialBalance: (state, action) => {
      state.trialBalance = action.payload;
    },
    setCashFlowStatement: (state, action) => {
      state.cashFlowStatement = action.payload;
    },
    setPaymentAccounts: (state, action) => {
      state.paymentAccounts = action.payload;
    },
    setGeneralLedger: (state, action) => {
      state.generalLedger = action.payload;
    },
    setDashboardData: (state, action) => {
      state.dashboardData = action.payload;
    },
    
    // Period Management
    setCurrentPeriod: (state, action) => {
      state.currentPeriod = action.payload;
    },
    setFinancialPeriods: (state, action) => {
      state.financialPeriods = action.payload;
    },
    
    // UI State actions
    setLoading: (state, action) => {
      state.loading = { ...state.loading, ...action.payload };
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    
    // Filter actions
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        dateRange: { start: null, end: null },
        accountType: 'all',
        status: 'all',
        period: 'current'
      };
    },
    
    // Print actions
    setPrintData: (state, action) => {
      state.printData = { ...state.printData, ...action.payload };
    },
    clearPrintData: (state) => {
      state.printData = {
        balanceSheet: null,
        incomeStatement: null,
        trialBalance: null,
        cashFlow: null,
        paymentAccounts: null
      };
    },
    
    // Reset all data
    resetAccounting: (state) => {
      return initialState;
    }
  }
});

export const {
  setChartOfAccounts,
  addAccount,
  updateAccount,
  deleteAccount,
  setJournalEntries,
  addJournalEntry,
  updateJournalEntry,
  deleteJournalEntry,
  setCurrentEntry,
  setBalanceSheet,
  setIncomeStatement,
  setTrialBalance,
  setCashFlowStatement,
  setPaymentAccounts,
  setGeneralLedger,
  setDashboardData,
  setCurrentPeriod,
  setFinancialPeriods,
  setLoading,
  setError,
  clearError,
  setFilters,
  clearFilters,
  setPrintData,
  clearPrintData,
  resetAccounting
} = accountingSlice.actions;

export default accountingSlice.reducer; 